import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, MessageSquare, Phone, Bell, AlertTriangle } from "lucide-react";
import {
  NOTIFICATION_EVENTS,
  CATEGORY_LABELS,
  eventsForRole,
  type NotificationCategory,
  type NotificationEventDef,
} from "@/lib/notificationEvents";
import { PhoneVerificationDialog } from "@/components/settings/PhoneVerificationDialog";
import { SMSConsentModal } from "@/components/settings/SMSConsentModal";
import { PushStatusCard } from "@/components/settings/PushStatusCard";

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const v = String(h).padStart(2, "0") + ":00";
  const label = `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`;
  return { value: v, label };
});

const TZ_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Phoenix", label: "Arizona Time — no daylight saving" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
];

interface Props { profile: any; onUpdated: (next: any) => void; }

export function NotificationPreferences({ profile, onUpdated }: Props) {
  const { user, isAdmin, refreshProfile } = useAuth();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [pendingConsentId, setPendingConsentId] = useState<string | null>(null);
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  

  const role = profile?.user_type as string | null;
  const events = useMemo(() => eventsForRole(role), [role]);
  const grouped = useMemo(() => {
    const m = new Map<NotificationCategory, NotificationEventDef[]>();
    events.forEach((e) => {
      const arr = m.get(e.category) ?? [];
      arr.push(e);
      m.set(e.category, arr);
    });
    return m;
  }, [events]);

  const emailMaster = profile?.notification_email ?? true;
  const smsMaster = profile?.notification_sms_enabled ?? false;
  const pushMaster = profile?.notification_push_enabled ?? true;
  const allChannelsOff = !emailMaster && !smsMaster && !pushMaster;
  const phoneVerified = !!profile?.phone_verified;
  const emailPrefs = (profile?.email_notification_preferences ?? {}) as Record<string, boolean>;
  const smsPrefs = (profile?.sms_notification_preferences ?? {}) as Record<string, boolean>;
  const pushPrefs = (profile?.push_notification_preferences ?? {}) as Record<string, boolean>;

  const persist = async (patch: Record<string, any>) => {
    if (!user) return;
    const prev = { ...profile };
    const next = { ...profile, ...patch };
    onUpdated(next);
    const { error } = await supabase.from("profiles").update(patch).eq("id", user.id);
    if (error) {
      toast.error("Failed to save preference");
      onUpdated(prev);
      return;
    }
    // Keep the shared auth profile (dashboard Alert Settings widget) in sync.
    if (typeof refreshProfile === "function") await refreshProfile();
  };

  const toggleChannel = (channel: "email" | "sms" | "push", on: boolean) => {
    if (channel === "sms" && on) {
      // Phone already verified → just enable SMS. No consent/OTP re-prompt.
      if (phoneVerified) {
        persist({ notification_sms_enabled: true });
        return;
      }
      // First-time enable — collect consent, then verify phone.
      setShowConsentModal(true);
      return;
    }
    if (channel === "email") persist({ notification_email: on });
    else if (channel === "sms") persist({ notification_sms_enabled: on });
    else persist({ notification_push_enabled: on });
  };

  const setEventEmail = (key: string, val: boolean) => {
    persist({ email_notification_preferences: { ...emailPrefs, [key]: val } });
  };
  const setEventSms = (key: string, val: boolean) => {
    persist({ sms_notification_preferences: { ...smsPrefs, [key]: val } });
  };
  const setEventPush = (key: string, val: boolean) => {
    persist({ push_notification_preferences: { ...pushPrefs, [key]: val } });
  };

  const setEventBoth = (e: NotificationEventDef, val: boolean) => {
    const patch: any = {
      email_notification_preferences: { ...emailPrefs, [e.key]: val },
    };
    if (e.smsCapable) patch.sms_notification_preferences = { ...smsPrefs, [e.key]: val };
    if (e.pushCapable) patch.push_notification_preferences = { ...pushPrefs, [e.key]: val };
    persist(patch);
  };

  const eventEffective = (e: NotificationEventDef) => {
    const emailVal = emailPrefs[e.key] === false ? false : (emailPrefs[e.key] === true ? true : e.defaultEmail);
    const smsVal = smsPrefs[e.key] === false ? false : (smsPrefs[e.key] === true ? true : e.defaultSms);
    const pushVal = pushPrefs[e.key] === false ? false : (pushPrefs[e.key] === true ? true : e.defaultPush);
    return { emailVal, smsVal, pushVal };
  };

  return (
    <div className="space-y-6">
      {allChannelsOff && (
        <div className="rounded-lg border-2 border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 dark:text-amber-100">
              You won't be notified about new loads, bids, or awards.
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
              You'll have to log in to see activity. Re-enable email to stay in the loop.
            </p>
          </div>
          <Button size="sm" onClick={() => toggleChannel("email", true)}>
            Re-enable email
          </Button>
        </div>
      )}

      {/* Push status diagnostic (Android) — admins only */}
      {isAdmin && <PushStatusCard />}

      {/* Channel selector */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-1">How do you want to be notified?</h2>
        <p className="text-sm text-muted-foreground mb-5">Pick the channels we should use for new activity.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <ChannelTile
            icon={<Bell className="w-5 h-5" />} title="App"
            subtitle="Home/lock-screen alerts on the app"
            on={pushMaster} onChange={(v) => toggleChannel("push", v)}
          />
          <ChannelTile
            icon={<Mail className="w-5 h-5" />} title="Email" subtitle="Always-on delivery"
            on={emailMaster} onChange={(v) => toggleChannel("email", v)}
          />
          <ChannelTile
            icon={<MessageSquare className="w-5 h-5" />} title="SMS"
            subtitle={phoneVerified ? "Awards, new loads, and quote requests" : "Requires phone verification"}
            on={smsMaster && phoneVerified} onChange={(v) => toggleChannel("sms", v)}
          />
          <ChannelTile
            icon={<Phone className="w-5 h-5" />} title="WhatsApp" subtitle="Coming soon"
            on={false} disabled comingSoon onChange={() => {}}
          />
        </div>
      </Card>

      {/* Quiet hours — SMS only */}
      {smsMaster && (
        <Card className="p-6">
          <h3 className="text-base font-semibold mb-1">Quiet hours</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Don't send SMS during these hours. Email is always delivered immediately. Critical events (bids, awards, ready dispatches) bypass quiet hours.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Start</Label>
              <Select value={profile?.notification_quiet_hours_start ?? "21:00"} onValueChange={(v) => persist({ notification_quiet_hours_start: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{HOUR_OPTIONS.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Select value={profile?.notification_quiet_hours_end ?? "07:00"} onValueChange={(v) => persist({ notification_quiet_hours_end: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{HOUR_OPTIONS.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Timezone</Label>
              <Select value={profile?.notification_timezone ?? "America/New_York"} onValueChange={(v) => persist({ notification_timezone: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TZ_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      )}

      {/* Event toggles grouped */}
      <Card className="p-6">
        <h3 className="text-base font-semibold mb-1">Notification events</h3>
        <p className="text-sm text-muted-foreground mb-5">Choose which events trigger a notification.</p>
        <div className="space-y-6">
          {(["critical", "opportunities", "active_loads", "network_messages", "updates"] as NotificationCategory[]).map((cat) => {
            const items = grouped.get(cat);
            if (!items?.length) return null;
            return (
              <div key={cat}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  {CATEGORY_LABELS[cat]}
                </h4>
                <div className="space-y-2">
                  {items.map((e) => {
                    const { emailVal, smsVal, pushVal } = eventEffective(e);
                    const mainOn = emailVal || smsVal || (pushVal && e.pushCapable);
                    return (
                      <div key={e.key} className="border border-border rounded-lg">
                        <div className="flex items-start justify-between gap-3 p-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Label className="font-medium">{e.label}</Label>
                              {e.critical && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-brand-teal bg-brand-teal/10 px-1.5 py-0.5 rounded">
                                  Critical
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{e.desc}</p>
                            {e.critical && !mainOn && (
                              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">⚠ You may miss important updates.</p>
                            )}
                          </div>
                          <div className="shrink-0">
                            <Switch
                              checked={mainOn}
                              onCheckedChange={(v) => setEventBoth(e, v)}
                            />
                          </div>
                        </div>
                        <div className="border-t border-border px-3 py-2 bg-muted/30 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                          <span className="text-muted-foreground">Send via:</span>
                          <label className={`flex items-center gap-1.5 ${e.pushCapable ? "cursor-pointer" : "opacity-40"}`}>
                            <Checkbox
                              checked={pushVal && pushMaster && e.pushCapable}
                              disabled={!pushMaster || !e.pushCapable}
                              onCheckedChange={(v) => setEventPush(e.key, !!v)}
                            />
                            App
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              checked={emailVal && emailMaster}
                              disabled={!emailMaster}
                              onCheckedChange={(v) => setEventEmail(e.key, !!v)}
                            />
                            Email
                          </label>
                          {e.smsCapable && (
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={smsVal && smsMaster}
                                disabled={!smsMaster}
                                onCheckedChange={(v) => setEventSms(e.key, !!v)}
                              />
                              SMS
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <SMSConsentModal
        open={showConsentModal}
        onOpenChange={setShowConsentModal}
        initialPhone={profile?.phone ?? null}
        onConsented={(consentId, phoneE164) => {
          setPendingConsentId(consentId);
          setPendingPhone(phoneE164);
          // Do NOT flip notification_sms_enabled yet — wait for real OTP verification.
          // The SMS tile is gated on phone_verified anyway, so any provisional flip
          // would just render as OFF and confuse the user on cancel.
          setShowPhoneDialog(true);
        }}
      />

      <PhoneVerificationDialog
        open={showPhoneDialog}
        onOpenChange={setShowPhoneDialog}
        defaultPhone={pendingPhone}
        onVerified={async () => {
          if (pendingConsentId) {
            await supabase.rpc("mark_sms_consent_otp_verified", { p_consent_id: pendingConsentId });
          }
          setPendingConsentId(null);
          setPendingPhone(null);
          // Only now — after phone_verified is true — enable the SMS master.
          await persist({ notification_sms_enabled: true });
          if (user) {
            const { data } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
            if (data) onUpdated(data);
          }
        }}
        onCancel={async () => {
          // OTP not completed — ensure SMS master stays OFF.
          setPendingConsentId(null);
          setPendingPhone(null);
          if (profile?.notification_sms_enabled) {
            await persist({ notification_sms_enabled: false });
          }
        }}
      />
    </div>
  );
}

interface TileProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  on: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  onChange: (v: boolean) => void;
}
function ChannelTile({ icon, title, subtitle, on, disabled, comingSoon, onChange }: TileProps) {
  return (
    <div className={`rounded-lg border p-4 flex flex-col gap-3 ${on ? "border-brand-teal bg-brand-teal/5" : "border-border bg-background"} ${disabled ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-2 font-medium ${on ? "text-brand-teal" : "text-foreground"}`}>
          {icon}
          {title}
        </div>
        {comingSoon ? (
          <span className="text-[10px] font-bold uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
            Soon
          </span>
        ) : (
          <Switch checked={on} disabled={disabled} onCheckedChange={onChange} />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
  );
}
